// The Eigenbloom look: the Harmonic Tunnel (web/src/skins/eigenbloom/, a prebuilt module from the
// eigenbloom project — never edited here) fills the whole window behind the interface, drawn live
// from the song everyone on the River is hearing: the same world-clock position and song loading
// as ZooRiver (lineup → loop → songEvents), the stream's tempo and the day's drum roles. Mounted
// once for the app (not in the editor, which plays its own music); the module (three.js inside,
// ~630 KB) loads only when someone picks this look, and destroy() frees its WebGL context.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  dayAt,
  KITS,
  laneSlot,
  SONG_TICKS,
  songEvents,
  TICKS_PER_BAR,
  type LoopData,
  type NoteEvent,
  type PeriodDef,
} from "@eb/shared";
import { clock as streamClock, STREAM_BPM } from "../stream";
import { useRiverPlayer } from "../river/context";
import { useSettings } from "../ui/settings";
import { useCalm } from "./zooScene";
import type { EigenbloomClock, EigenbloomRole, EigenbloomVisual } from "./eigenbloom/eigenbloom-live";

const { barAt, barContent, MS_PER_BAR } = streamClock;
type Module = typeof import("./eigenbloom/eigenbloom-live");

/** The module, loaded once (the backdrop and the River's shape picker share it). */
let modulePromise: Promise<Module> | null = null;
export function eigenbloomModule(): Promise<Module> {
  modulePromise ??= import("./eigenbloom/eigenbloom-live.js");
  modulePromise.catch(() => (modulePromise = null));
  return modulePromise;
}

/**
 * True when WebGL would be drawn in software (no usable GPU: SwiftShader, llvmpipe…). A full-window
 * tunnel at a few frames per second would slow the whole page down, so the look keeps its static
 * background there. `window.__ebForceEigenbloom` (tests) draws it anyway.
 */
function softwareRendered(): boolean {
  if ((window as unknown as { __ebForceEigenbloom?: boolean }).__ebForceEigenbloom) return false;
  try {
    const c = document.createElement("canvas");
    const gl = (c.getContext("webgl2") ?? c.getContext("webgl")) as WebGLRenderingContext | null;
    if (!gl) return true;
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : "";
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return /swiftshader|llvmpipe|software/i.test(renderer);
  } catch {
    return true;
  }
}

/** Visuals alive / destroyed on this page (e2e checks that switching away frees the WebGL context). */
const counts = { live: 0, destroyed: 0 };
(window as unknown as { __ebEigenbloom: typeof counts }).__ebEigenbloom = counts;

/** pad → role: the day's kit decides the drums; lane alternates count as their instrument. */
export function eigenbloomRole(kit: PeriodDef["kit"] | undefined): (pad: number) => EigenbloomRole {
  const r = kit ? KITS[kit].roles : null;
  return (pad) => {
    if (r) {
      if (pad === r.kick) return "kick";
      if (pad === r.snare) return "snare";
      if (pad === r.closed_hat) return "hat";
      if (pad === r.open_hat) return "openhat";
    }
    if (pad < 16) return "perc";
    if (pad >= 20 && pad < 32) return "atmos";
    const slot = laneSlot(pad);
    return slot === 16 ? "bass" : slot === 17 ? "harmony" : slot === 19 ? "classic" : "lead";
  };
}

export function EigenbloomBackdrop() {
  const host = useRef<HTMLDivElement>(null);
  const viz = useRef<EigenbloomVisual | null>(null);
  const [failed, setFailed] = useState(false);
  const player = useRiverPlayer();
  const calm = useCalm();
  const shape = useSettings((s) => s.shape);
  const [period, setPeriod] = useState<PeriodDef | null>(null);
  const day = player ? dayAt(player.worldNow(), player.epochMs()) : null;
  useEffect(() => {
    if (!player || day === null) return;
    let alive = true;
    void player.period(day).then((p) => alive && setPeriod(p));
    return () => {
      alive = false;
    };
  }, [player, day]);

  // The song sounding now (refreshed when the slot changes), exactly as in ZooRiver.
  const current = useMemo(() => ({ key: "", events: [] as readonly NoteEvent[] }), []);
  const clock: EigenbloomClock = useMemo(
    () => ({
      position() {
        if (!player) return { tick: 0, lengthTicks: SONG_TICKS, playing: false };
        const now = player.worldNow();
        const epoch = player.epochMs();
        const bar = barAt(now, epoch);
        const c = barContent(bar);
        const inBar = ((now - epoch - bar * MS_PER_BAR) / MS_PER_BAR) * TICKS_PER_BAR;
        if (c.kind !== "loop") return { tick: 0, lengthTicks: SONG_TICKS, playing: false };
        const key = `${c.day}:${c.slot}`;
        if (current.key !== key) {
          current.key = key;
          current.events = [];
          void player
            .lineup(c.day)
            .then((l) => player.loop(l.entries[c.slot].loopId))
            .then((loop?: LoopData) => {
              if (current.key === key && loop) current.events = songEvents(loop);
            })
            .catch(() => undefined);
        }
        return { tick: c.loopBar * TICKS_PER_BAR + inBar, lengthTicks: SONG_TICKS, playing: true };
      },
      events: () => current.events,
      bpm: () => STREAM_BPM,
    }),
    [player, current],
  );
  const roleOf = useMemo(() => eigenbloomRole(period?.kit), [period?.kit]);

  // Latest values for the one-time mount below.
  const latest = useRef({ clock, roleOf, calm, shape });
  latest.current = { clock, roleOf, calm, shape };

  useEffect(() => {
    let alive = true;
    if (softwareRendered()) {
      setFailed(true);
      return;
    }
    void eigenbloomModule()
      .then((m) => {
        if (!alive || !host.current) return;
        const { clock, roleOf, calm, shape } = latest.current;
        try {
          viz.current = m.createEigenbloomVisual(host.current, {
            clock,
            roleOf,
            calm,
            shape: m.shapes.some((s) => s.id === shape) ? shape : "heart",
          });
          counts.live++;
        } catch {
          setFailed(true); // no WebGL here: the look's static background stays
        }
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (viz.current) {
        viz.current.destroy();
        viz.current = null;
        counts.live--;
        counts.destroyed++;
      }
    };
  }, []);

  useEffect(() => viz.current?.setClock(clock), [clock]);
  useEffect(() => viz.current?.setRoleOf(roleOf), [roleOf]);
  useEffect(() => viz.current?.setCalm(calm), [calm]);
  useEffect(() => void viz.current?.setShape(shape), [shape]);

  // A hidden tab rests.
  useEffect(() => {
    const onVis = () => (document.hidden ? viz.current?.pause() : viz.current?.resume());
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  return (
    <div
      ref={host}
      className={`eigenbloom-backdrop${failed ? " failed" : ""}`}
      data-testid="eigenbloom-stage"
      aria-hidden="true"
    />
  );
}
